/**
 * @fileoverview Core Icon Set - Essential starter icons for the UI Kit
 * @module icons/core-icons
 * @version 1.0.0
 * 
 * @description
 * A curated set of commonly-used icons for everyday UI needs.
 * These icons are automatically registered and available for use with ctl-icon.
 * 
 * Usage:
 *   import '@cliftontechlabs/ui-kit/icons';
 *   <ctl-icon name="check"></ctl-icon>
 * 
 * All icons are 24x24 viewBox and use currentColor for theming.
 */

// Define base SVG strings for icons with multiple names (aliases)
const PACKAGE_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a1 1 0 0 1 .5.1l8 4.5a1 1 0 0 1 .5.9v9a1 1 0 0 1-.5.9l-8 4.5a1 1 0 0 1-1 0l-8-4.5a1 1 0 0 1-.5-.9v-9a1 1 0 0 1 .5-.9l8-4.5A1 1 0 0 1 12 2zm0 2.3L5 8.5v7.8l6 3.4V12a1 1 0 1 1 2 0v7.7l6-3.4V8.5l-7-4.2z"/>
  </svg>
`;

const ZAP_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13 2a1 1 0 0 1 .9 1.4L10.7 11H17a1 1 0 0 1 .8 1.6l-7 10a1 1 0 0 1-1.7-1L12.3 13H7a1 1 0 0 1-.9-1.4l6-9A1 1 0 0 1 13 2z"/>
  </svg>
`;

const CLOSE_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.3 6.3a1 1 0 0 1 1.4 0L12 10.6l4.3-4.3a1 1 0 1 1 1.4 1.4L13.4 12l4.3 4.3a1 1 0 0 1-1.4 1.4L12 13.4l-4.3 4.3a1 1 0 0 1-1.4-1.4L10.6 12 6.3 7.7a1 1 0 0 1 0-1.4z"/>
  </svg>
`;

const SETTINGS_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm-2 4a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"/>
    <path d="M10.8 3.5a1.5 1.5 0 0 1 2.4 0l.8 1a1.5 1.5 0 0 0 1.1.6l1.2.1a1.5 1.5 0 0 1 1.4 1.8l-.3 1.2a1.5 1.5 0 0 0 .2 1.2l.6 1a1.5 1.5 0 0 1-.6 2.1l-1 .6a1.5 1.5 0 0 0-.7 1l-.2 1.2a1.5 1.5 0 0 1-2 1.1l-1.1-.4a1.5 1.5 0 0 0-1.2 0l-1.1.4a1.5 1.5 0 0 1-2-1.1l-.2-1.2a1.5 1.5 0 0 0-.7-1l-1-.6a1.5 1.5 0 0 1-.6-2.1l.6-1a1.5 1.5 0 0 0 .2-1.2l-.3-1.2a1.5 1.5 0 0 1 1.4-1.8l1.2-.1a1.5 1.5 0 0 0 1.1-.6l.8-1z"/>
  </svg>
`;

const INFO_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 8a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1zm0-4a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 12 6z"/>
  </svg>
`;

const MENU_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1z"/>
  </svg>
`;

const WARNING_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M10.3 3.5a2 2 0 0 1 3.4 0l8.5 13A2 2 0 0 1 20.5 19h-17a2 2 0 0 1-1.7-2.5l8.5-13zM12 9a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1zm0 8a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 17z"/>
  </svg>
`;

const ERROR_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 6a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1zm0 10a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 18z"/>
  </svg>
`;

const BELL_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a1 1 0 0 1 1 1v1.2A6 6 0 0 1 18 10v5l1.7 1.7a1 1 0 0 1-.7 1.7H5a1 1 0 0 1-.7-1.7L6 15v-5a6 6 0 0 1 5-5.9V3a1 1 0 0 1 1-1zm-2 18a2 2 0 1 0 4 0h-4z"/>
  </svg>
`;

const USERS_ICON_SVG = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM4 9a5 5 0 1 1 10 0A5 5 0 0 1 4 9zm13-3a1 1 0 1 0 0 2 2 2 0 0 1 2 2 1 1 0 1 0 2 0 4 4 0 0 0-4-4zM3 19a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4 1 1 0 1 1-2 0 2 2 0 0 0-2-2H7a2 2 0 0 0-2 2 1 1 0 1 1-2 0zm14-1a1 1 0 1 0 0 2 1 1 0 0 0 1-1 1 1 0 1 0 2 0 3 3 0 0 1-3 3 1 1 0 1 0 0-2z"/>
  </svg>
`;

export const CORE_ICONS = {
  // Navigation & Directional
  "chevron-up": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.3 14.7a1 1 0 0 1-1.4 0L12 10.8l-3.9 3.9a1 1 0 1 1-1.4-1.4l4.6-4.6a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4z"/>
    </svg>
  `,
  "chevron-down": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.2l3.9-3.9a1 1 0 1 1 1.4 1.4l-4.6 4.6a1 1 0 0 1-1.4 0L6.7 10.7a1 1 0 0 1 0-1.4z"/>
    </svg>
  `,
  "chevron-left": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.7 6.7a1 1 0 0 1 0 1.4L10.8 12l3.9 3.9a1 1 0 1 1-1.4 1.4l-4.6-4.6a1 1 0 0 1 0-1.4l4.6-4.6a1 1 0 0 1 1.4 0z"/>
    </svg>
  `,
  "chevron-right": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.3 6.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4l-4.6 4.6a1 1 0 0 1-1.4-1.4l3.9-3.9-3.9-3.9a1 1 0 0 1 0-1.4z"/>
    </svg>
  `,
  "arrow-up": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a1 1 0 0 1 .7.3l5 5a1 1 0 0 1-1.4 1.4L13 7.4V19a1 1 0 1 1-2 0V7.4l-3.3 3.3a1 1 0 0 1-1.4-1.4l5-5A1 1 0 0 1 12 4z"/>
    </svg>
  `,
  "arrow-down": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20a1 1 0 0 1-.7-.3l-5-5a1 1 0 0 1 1.4-1.4L11 16.6V5a1 1 0 1 1 2 0v11.6l3.3-3.3a1 1 0 0 1 1.4 1.4l-5 5a1 1 0 0 1-.7.3z"/>
    </svg>
  `,
  "arrow-left": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 12a1 1 0 0 1 .3-.7l5-5a1 1 0 0 1 1.4 1.4L7.4 11H19a1 1 0 1 1 0 2H7.4l3.3 3.3a1 1 0 0 1-1.4 1.4l-5-5A1 1 0 0 1 4 12z"/>
    </svg>
  `,
  "arrow-right": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 12a1 1 0 0 1-.3.7l-5 5a1 1 0 0 1-1.4-1.4l3.3-3.3H5a1 1 0 1 1 0-2h11.6l-3.3-3.3a1 1 0 0 1 1.4-1.4l5 5a1 1 0 0 1 .3.7z"/>
    </svg>
  `,

  // Actions & Controls
  "check": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 16.17l-3.88-3.88a1 1 0 1 0-1.41 1.41l4.59 4.59a1 1 0 0 0 1.41 0l10-10a1 1 0 0 0-1.41-1.41L9 16.17z"/>
    </svg>
  `,
  "x": CLOSE_ICON_SVG,
  "close": CLOSE_ICON_SVG,
  "plus": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a1 1 0 0 1 1 1v6h6a1 1 0 1 1 0 2h-6v6a1 1 0 1 1-2 0v-6H5a1 1 0 1 1 0-2h6V5a1 1 0 0 1 1-1z"/>
    </svg>
  `,
  "minus": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "edit": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16.7 3.3a1 1 0 0 1 1.4 0l2.6 2.6a1 1 0 0 1 0 1.4l-11 11a1 1 0 0 1-.5.3l-4 1a1 1 0 0 1-1.2-1.2l1-4a1 1 0 0 1 .3-.5l11-11zm0 2.8l-10 10-.5 2 2-.5 10-10-1.5-1.5z"/>
    </svg>
  `,
  "trash": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h4a1 1 0 1 1 0 2h-1v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6H5a1 1 0 0 1 0-2h4V3zm1 3v11a1 1 0 1 0 2 0V6a1 1 0 1 0-2 0zm4 0v11a1 1 0 1 0 2 0V6a1 1 0 1 0-2 0z"/>
    </svg>
  `,
  "copy": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H8zm0 2h8v10H8V6zm-2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2h-2v2H6v-6h2v-2z"/>
    </svg>
  `,
  "download": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a1 1 0 0 1 1 1v9.6l3.3-3.3a1 1 0 0 1 1.4 1.4l-5 5a1 1 0 0 1-1.4 0l-5-5a1 1 0 0 1 1.4-1.4L11 14.6V5a1 1 0 0 1 1-1zM5 19a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "upload": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a1 1 0 0 1 .7.3l5 5a1 1 0 0 1-1.4 1.4L13 7.4V17a1 1 0 1 1-2 0V7.4l-3.3 3.3a1 1 0 0 1-1.4-1.4l5-5A1 1 0 0 1 12 4zM5 19a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "search": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12zm-8 6a8 8 0 1 1 14.32 4.9l4.39 4.39a1 1 0 0 1-1.42 1.42l-4.38-4.39A8 8 0 0 1 2 10z"/>
    </svg>
  `,
  "settings": SETTINGS_ICON_SVG,
  "gear": SETTINGS_ICON_SVG,

  // Status & Feedback
  "info": INFO_ICON_SVG,
  "info-circle": INFO_ICON_SVG,
  "warning": WARNING_ICON_SVG,
  "alert-triangle": WARNING_ICON_SVG,
  "error": ERROR_ICON_SVG,
  "exclamation-circle": ERROR_ICON_SVG,
  "question-circle": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 6a2 2 0 0 1 1.7 3.1l-.7.9a1 1 0 0 0-.3.7v.3a1 1 0 1 1-2 0v-.3a3 3 0 0 1 .9-2.1l.7-.9A.5.5 0 0 0 12 8a1 1 0 1 1 0-2zm0 10a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 18z"/>
    </svg>
  `,
  "check-circle": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.7 8.3a1 1 0 0 0-1.4-1.4L11 13.2l-1.3-1.3a1 1 0 0 0-1.4 1.4l2 2a1 1 0 0 0 1.4 0l5-5z"/>
    </svg>
  `,

  // Menu & Navigation
  "menu": MENU_ICON_SVG,
  "bars": MENU_ICON_SVG,
  "dots": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
  `,
  "external": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 5a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V6.4l-7.3 7.3a1 1 0 0 1-1.4-1.4L16.6 5H13zM5 7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3a1 1 0 1 1 2 0v3a4 4 0 0 1-4 4H5a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4h3a1 1 0 1 1 0 2H5z"/>
    </svg>
  `,
  "home": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M11.3 2.3a1 1 0 0 1 1.4 0l9 9a1 1 0 0 1-1.4 1.4l-.3-.3V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6.6l-.3.3a1 1 0 0 1-1.4-1.4l9-9zM6 10.4V19h3v-4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4h3v-8.6l-6-6-6 6z"/>
    </svg>
  `,

  // Media & Content
  "star": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.5l2.9 6 6.6 1-4.8 4.7 1.1 6.6-5.8-3.1-5.8 3.1 1.1-6.6L2.5 9.5l6.6-1 2.9-6z"/>
    </svg>
  `,
  "heart": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21a1 1 0 0 1-.7-.3l-7-7A5 5 0 0 1 11.4 6l.6.6.6-.6A5 5 0 0 1 19.7 13.7l-7 7a1 1 0 0 1-.7.3z"/>
    </svg>
  `,
  "eye": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5C7 5 2.7 8.1 1 12.5c1.7 4.4 6 7.5 11 7.5s9.3-3.1 11-7.5C21.3 8.1 17 5 12 5zm0 12a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z"/>
    </svg>
  `,
  "eye-off": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.7 2.3a1 1 0 0 0-1.4 1.4l3.5 3.5A12 12 0 0 0 1 12.5c1.7 4.4 6 7.5 11 7.5 2 0 3.9-.5 5.5-1.3l3.8 3.8a1 1 0 0 0 1.4-1.4l-18-18zM12 17c-1.2 0-2.3-.3-3.3-.9l1.5-1.5a2.5 2.5 0 0 0 3.3-3.3l1.5-1.5A4.5 4.5 0 0 1 12 17zm0-7c.4 0 .9.1 1.2.3l5.3-5.3A12 12 0 0 0 12 4C7 4 2.7 7.1 1 11.5c.6 1.4 1.4 2.6 2.5 3.7L7 11.7c.2-.3.6-.6 1-.8A2.5 2.5 0 0 1 12 10z"/>
    </svg>
  `,
  "mail": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6zm2 0l7 4.5L19 6H5zm0 2.3V18h14V8.3l-6.5 4.2a1 1 0 0 1-1 0L5 8.3z"/>
    </svg>
  `,
  "bell": BELL_ICON_SVG,
  "calendar": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1zM5 10v9h14v-9H5z"/>
    </svg>
  `,
  "clock": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 5a1 1 0 1 0-2 0v5a1 1 0 0 0 .3.7l3 3a1 1 0 0 0 1.4-1.4L13 11.6V7z"/>
    </svg>
  `,
  "user": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM6 8a6 6 0 1 1 12 0A6 6 0 0 1 6 8zm-2 12a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5 1 1 0 1 1-2 0 3 3 0 0 0-3-3H9a3 3 0 0 0-3 3 1 1 0 1 1-2 0z"/>
    </svg>
  `,
  "users": USERS_ICON_SVG,
  "folder": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8L9.4 4.3A1 1 0 0 0 8.6 4H4z"/>
    </svg>
  `,
  "file": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6H7zm0 2h5v5h5v11H7V4z"/>
    </svg>
  `,

  // Loading & States  
  "spinner": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a1 1 0 0 1 0 2 8 8 0 1 0 8 8 1 1 0 1 1 2 0 10 10 0 1 1-10-10z"/>
    </svg>
  `,
  "refresh": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.5 12a7.5 7.5 0 0 1 13-5l-3 3h7V3l-2.6 2.6A9.5 9.5 0 1 0 21.5 12h-2a7.5 7.5 0 0 1-15 0z"/>
    </svg>
  `,
  "lock": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a5 5 0 0 1 5 5v2h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h1V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v2h6V7a3 3 0 0 0-3-3z"/>
    </svg>
  `,
  "unlock": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a5 5 0 0 1 5 5v2h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h9V7a3 3 0 0 0-3-3 3 3 0 0 0-3 2.8 1 1 0 1 1-2-.2A5 5 0 0 1 12 2z"/>
    </svg>
  `,
  "share": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 8a3 3 0 1 0-2.8-2H8.7a6 6 0 1 0 0 12h6.5a3 3 0 1 0-.2-2H8.7a4 4 0 1 1 0-8h6.5A3 3 0 0 0 18 8zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
    </svg>
  `,
  "link": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 6H7a5 5 0 0 0 0 10h3a1 1 0 1 1 0 2H7A7 7 0 0 1 7 4h3a1 1 0 1 1 0 2zm7 0h-3a1 1 0 1 1 0-2h3a7 7 0 0 1 0 14h-3a1 1 0 1 1 0-2h3a5 5 0 0 0 0-10zm-9 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "filter": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 13v6a1 1 0 0 1-.4.8l-4 3A1 1 0 0 1 8 22v-9L1.2 4.6A1 1 0 0 1 3 4z"/>
    </svg>
  `,
  "sort": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5a1 1 0 0 1 .7.3l3 3a1 1 0 0 1-1.4 1.4L9 8.4V19a1 1 0 1 1-2 0V8.4l-1.3 1.3a1 1 0 0 1-1.4-1.4l3-3A1 1 0 0 1 8 5zm8 14a1 1 0 0 1-.7-.3l-3-3a1 1 0 0 1 1.4-1.4l1.3 1.3V5a1 1 0 1 1 2 0v10.6l1.3-1.3a1 1 0 0 1 1.4 1.4l-3 3a1 1 0 0 1-.7.3z"/>
    </svg>
  `,
  "image": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5zm0 2h14v8.6l-3.3-3.3a1 1 0 0 0-1.4 0L8.6 16l-1.3-1.3a1 1 0 0 0-1.4 0L5 15.6V5zm3 4a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
  `,
  "play": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5v14l11-7z"/>
    </svg>
  `,
  "pause": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
    </svg>
  `,
  "palette": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 0 1 10 10c0 2.4-1.6 4-4 4h-2.4a1 1 0 0 0-1 1c0 .3.1.6.3.8.2.3.4.6.4 1a2 2 0 0 1-2 2A10 10 0 0 1 2 12 10 10 0 0 1 12 2zm0 2a8 8 0 0 0-8 8 8 8 0 0 0 8 8 .5.5 0 0 0 0-1c-.2-.2-.3-.4-.3-.6a3 3 0 0 1 3-3H18c1.4 0 2-.6 2-2a8 8 0 0 0-8-8zM8 10a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm8 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM9 16a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
  `,
  "medical": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a2 2 0 0 1 2 2v4h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4v4a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-4H2a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h4V4a2 2 0 0 1 2-2h4zm0 2H8v6H2v4h6v6h4v-6h6v-4h-6V4z"/>
    </svg>
  `,
  "test-tube": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.5 2a1 1 0 0 1 1 1v.5h1a1 1 0 1 1 0 2h-1v9a6 6 0 0 1-12 0v-9h-1a1 1 0 0 1 0-2h1V3a1 1 0 1 1 2 0v.5h7V3a1 1 0 0 1 1-1zm-1 3.5h-7v9a4 4 0 0 0 8 0v-9zm-5 6a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"/>
    </svg>
  `,
  "target": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 1a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
    </svg>
  `,
  "sliders": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7a1 1 0 0 1 1-1h3.6a3 3 0 0 1 5.8 0H19a1 1 0 1 1 0 2h-4.6a3 3 0 0 1-5.8 0H5a1 1 0 0 1-1-1zm7 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0zm-7 5a1 1 0 0 1 1-1h4.6a3 3 0 0 1 5.8 0H19a1 1 0 1 1 0 2h-4.6a3 3 0 0 1-5.8 0H5a1 1 0 0 1-1-1zm10 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0zm-10 5a1 1 0 0 1 1-1h3.6a3 3 0 0 1 5.8 0H19a1 1 0 1 1 0 2h-4.6a3 3 0 0 1-5.8 0H5a1 1 0 0 1-1-1zm7 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0z"/>
    </svg>
  `,
  "puzzle": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 3a2 2 0 0 0-2 2v6a1 1 0 0 0 1 1h1a2 2 0 0 1 2 2 2 2 0 0 1-2 2H3a1 1 0 0 0-1 1v4a2 2 0 0 0 2 2h4a1 1 0 0 0 1-1v-1a2 2 0 0 1 2-2 2 2 0 0 1 2 2v1a1 1 0 0 0 1 1h6a2 2 0 0 0 2-2v-6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2-2 2 2 0 0 1 2-2h1a1 1 0 0 0 1-1V5a2 2 0 0 0-2-2h-6a1 1 0 0 0-1 1v1a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a1 1 0 0 0-1-1H4z"/>
    </svg>
  `,
  "building": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4zm0 2h7v16H4V4zm9 0h7v16h-7V4zM6 7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1H6zm9 0a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-2zM6 13a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H6zm9 0a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1h-2z"/>
    </svg>
  `,
  "accessibility": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-5 6a1 1 0 0 1 1-1h8a1 1 0 0 1 .8 1.6l-2 3a1 1 0 0 1-.3.3v4.1a1 1 0 1 1-2 0v-3h-1v3a1 1 0 1 1-2 0v-4.1a1 1 0 0 1-.3-.3l-2-3A1 1 0 0 1 7 12z"/>
    </svg>
  `,
  "package": PACKAGE_ICON_SVG,
  "brain": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 4a4 4 0 0 0-4 4 4 4 0 0 0-2 3.5A3.5 3.5 0 0 0 6.5 15c.4 0 .7-.1 1-.2V17a3 3 0 0 0 3 3h3a3 3 0 0 0 3-3v-2.2c.3.1.6.2 1 .2a3.5 3.5 0 0 0 3.5-3.5A4 4 0 0 0 19 8a4 4 0 0 0-4-4c-.6 0-1.2.1-1.7.4A4 4 0 0 0 12 4a4 4 0 0 0-1.3.2C10.2 4.1 9.6 4 9 4zm0 2c.4 0 .8.1 1.2.2A2 2 0 0 1 12 6a2 2 0 0 1 1.8.2c.4-.1.8-.2 1.2-.2a2 2 0 0 1 2 2 2 2 0 0 1-.2 1 2 2 0 0 1 1.2 1.8 1.5 1.5 0 0 1-1.5 1.5c-.4 0-.8-.2-1-.4V17a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-4.1c-.2.2-.6.4-1 .4A1.5 1.5 0 0 1 5 11.5c0-.7.4-1.4 1.2-1.8A2 2 0 0 1 7 8a2 2 0 0 1 2-2z"/>
    </svg>
  `,
  "flag": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3a1 1 0 0 1 1-1 1 1 0 0 1 1 1v1h11a1 1 0 0 1 .8 1.6L16.5 9l2.3 3.4A1 1 0 0 1 18 14H7v7a1 1 0 1 1-2 0V3zm2 2v7h9.4l-1.8-2.6a1 1 0 0 1 0-1.2L16.4 5H7z"/>
    </svg>
  `,
  "wrench": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 2a5.5 5.5 0 0 1 4.6 8.5l1.6 1.6a1 1 0 0 1 0 1.4l-4 4a1 1 0 0 1-1.4 0l-1.6-1.6A5.5 5.5 0 0 1 6.2 7.4l3.1 3.1a1 1 0 0 0 1.4 0l2.8-2.8a1 1 0 0 0 0-1.4L10.4 3.2A5.5 5.5 0 0 1 15.5 2zm0 2a3.5 3.5 0 0 0-2.8 1.4l1.5 1.5-2.8 2.8L9.9 8.2A3.5 3.5 0 0 0 17 13.1l2-2-1.3-1.3a1 1 0 0 1 0-1.4l1-1A3.5 3.5 0 0 0 15.5 4z"/>
    </svg>
  `,
  "ruler": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7zm2 0v10h2v-3a1 1 0 1 1 2 0v3h2v-2a1 1 0 1 1 2 0v2h2v-3a1 1 0 1 1 2 0v3h2v-2a1 1 0 1 1 2 0v2h2V7H5z"/>
    </svg>
  `,
  "square": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5zm0 2h14v14H5V5z"/>
    </svg>
  `,
  "moon": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 2a1 1 0 0 1 .7 1.7 8 8 0 1 0 9.6 9.6A1 1 0 0 1 22 14a10 10 0 1 1-12-12z"/>
    </svg>
  `,
  "zap": ZAP_ICON_SVG,
  "lightning": ZAP_ICON_SVG,

  // ── Admin & Navigation Icons ────────────────────────────────────────────

  "activity": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>
  `,
  "file-text": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6H7zm0 2h5v5h5v11H7V4zm2 8a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H9z"/>
    </svg>
  `,
  "map": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a7 7 0 0 1 7 7c0 4.5-7 13-7 13S5 13.5 5 9a7 7 0 0 1 7-7zm0 2a5 5 0 0 0-5 5c0 3.1 4.1 8.8 5 10 .9-1.2 5-6.9 5-10a5 5 0 0 0-5-5zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
    </svg>
  `,
  "credit-card": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6zm2 0v2h14V6H5zm0 4v8h14v-8H5zm2 3a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "clipboard": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 2a1 1 0 0 0-1 1H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2a1 1 0 0 0-1-1H9zm0 2h6v1H9V4zm-3 1h2v1a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V5h2v15H6V5zm2 5a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2H8zm0 4a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2H8zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H8z"/>
    </svg>
  `,
  "box": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4zm0 2h16v2H4V6zm0 4h16v8H4v-8zm2 2a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H6z"/>
    </svg>
  `,
  "user-plus": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 8a6 6 0 1 1 12 0A6 6 0 0 1 4 8zm-2 12a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5 1 1 0 1 1-2 0 3 3 0 0 0-3-3H7a3 3 0 0 0-3 3 1 1 0 1 1-2 0zm17-9a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1z"/>
    </svg>
  `,
  "dashboard": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H3zm0 2h5v5H3V5zm11-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-7zm0 2h5v1h-5V5zM3 15a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H3zm0 2h5v2H3v-2zm11 2a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1h-7zm0 2h5v1h-5v-1zm0-10a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1h-7zm0 2h5v3h-5v-3z"/>
    </svg>
  `,
  "orders": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H7zm0 2h10v16H7V4zm2 3a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9zm0 4a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H9z"/>
    </svg>
  `,
  "discounts": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12.6 2.2a1 1 0 0 0-1.2 0l-9 7A1 1 0 0 0 2 10v11a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V10a1 1 0 0 0-.4-.8l-9-7zM12 4.3L20 10.6V20H4v-9.4L12 4.3zM9 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm5.3.3a1 1 0 0 0-1.4 1.4l2 2a1 1 0 0 0 1.4-1.4l-2-2zM15 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
    </svg>
  `,
  "reports": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H5zm0 2h14v16H5V4zm7 3a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1zm4 3a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0v-5a1 1 0 0 1 1-1zM8 13a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1z"/>
    </svg>
  `,
  "more-vertical": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
  `,
  "list": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1zm0 6a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1zm0 6a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1z"/>
    </svg>
  `,
  "dot": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/>
    </svg>
  `,
  "alert-circle": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 4a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1zm0 8a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 12 16z"/>
    </svg>
  `,
  "arrow": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 12a1 1 0 0 1-.3.7l-5 5a1 1 0 0 1-1.4-1.4l3.3-3.3H5a1 1 0 1 1 0-2h11.6l-3.3-3.3a1 1 0 0 1 1.4-1.4l5 5a1 1 0 0 1 .3.7z"/>
    </svg>
  `,

  // ── Aliases ─────────────────────────────────────────────────────────────
  // These map common alternative names to existing icon SVGs

  "help-circle": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 4a2 2 0 0 1 1.7 3.1l-.7.9a1 1 0 0 0-.3.7v.3a1 1 0 1 1-2 0v-.3a3 3 0 0 1 .9-2.1l.7-.9A.5.5 0 0 0 12 8a1 1 0 1 1 0-2zm0 10a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 18z"/>
    </svg>
  `,
  "cog": SETTINGS_ICON_SVG,
  "notifications": BELL_ICON_SVG,
  "warn": WARNING_ICON_SVG,
  "exclamation-triangle": WARNING_ICON_SVG,
  "bolt": ZAP_ICON_SVG,
  "view": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5C7 5 2.7 8.1 1 12.5c1.7 4.4 6 7.5 11 7.5s9.3-3.1 11-7.5C21.3 8.1 17 5 12 5zm0 12a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z"/>
    </svg>
  `,
  "customers": USERS_ICON_SVG,
  "products": PACKAGE_ICON_SVG,
  "content": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6H7zm0 2h5v5h5v11H7V4zm2 8a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H9z"/>
    </svg>
  `,
  "spark": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z"/>
    </svg>
  `,
  "server": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4zm2 0v4h14V4H5zm14 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM3 14a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4zm2 0v4h14v-4H5zm14 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
    </svg>
  `,
};
